// ContinueCard.js
// Hero "Continue where you left off" card. Resume-state parsing copied from
// ResumeCard.js:39-91, reshaped into the crumb/title split from
// docs/superpowers/specs/2026-09-16-home-redesign.md §5 (Student > ContinueCard).
import React from "react";
import { View } from "react-native";

import HomeCard from "../primitives/HomeCard";
import HomeRow from "../primitives/HomeRow";
import { formatPwoName } from "../../../utils/formatSupSub";

// The English prefix the backend writes at the start of `summary_stub`.
const YOU_ASKED_PREFIX = "You asked: ";

/** Reads the video/PWO resume state into { icon, title, crumb }, or null. */
const parseLearningResume = (learningResumeState, t) => {
  const assetType = learningResumeState?.asset_type?.toUpperCase?.() || "";
  const stateJson = learningResumeState?.state_json || {};

  if (assetType !== "VIDEO" && assetType !== "PWO") return null;

  const chapterName = stateJson?.chapter_name || "";
  const topicName = stateJson?.topic_name || "";
  const crumb = [chapterName, topicName].filter(Boolean).join(" · ");

  if (assetType === "VIDEO") {
    const videoName =
      stateJson?.video_name ||
      stateJson?.tag_name ||
      stateJson?.collection_name ||
      t("Resume where you left off");
    return { icon: "play-circle-outline", title: videoName, crumb };
  }

  // PWO: match the "pwo_name - scene_label" format used elsewhere in the app.
  const pwoName = formatPwoName(stateJson?.pwo_name || "");
  const sceneLabel = formatPwoName(stateJson?.pwo_scene_label || stateJson?.pwo_scene_name || "");
  const pwoTitle =
    pwoName && sceneLabel
      ? `${pwoName} - ${sceneLabel}`
      : pwoName || sceneLabel || stateJson?.collection_name || t("Resume simulation");
  return { icon: "cube-outline", title: pwoTitle, crumb };
};

/** Reads the curiosity AI resume state into { title, subtitle }, or null. */
const parseCuriosityResume = (curiosityResumeState, t) => {
  const aiPointer = curiosityResumeState?.ai_pointer || {};
  if (!aiPointer?.conversation_id) return null;

  const stub = aiPointer?.summary_stub;
  const truncate = (text) => (text.length > 60 ? `${text.substring(0, 57)}...` : text);
  let subtitle;
  if (!stub) {
    subtitle = truncate(t("Continue your conversation"));
  } else if (stub.startsWith(YOU_ASKED_PREFIX)) {
    // The stored stub carries an English prefix; re-render it in the user's
    // language around the question itself.
    subtitle = t("You asked: {question}", {
      question: truncate(stub.substring(YOU_ASKED_PREFIX.length)),
    });
  } else {
    subtitle = truncate(stub);
  }
  return { title: t("Pick up your chat with Saras"), subtitle };
};

const ContinueCard = ({
  learningResumeState = null,
  curiosityResumeState = null,
  onContinueLearning,
  onContinueCuriosityAI,
  onBrowseTopics,
  isDarkMode = false,
  t = (s) => s,
}) => {
  const learning = parseLearningResume(learningResumeState, t);
  const curiosity = parseCuriosityResume(curiosityResumeState, t);
  const hasContent = !!learning || !!curiosity;

  return (
    <HomeCard title={t("Continue where you left off")} hero isDarkMode={isDarkMode} testID="continue-card">
      {!hasContent ? (
        <HomeRow
          first
          title={t("Start learning")}
          subtitle={t("Browse topics to begin your journey")}
          action={{ label: t("Explore topics"), onPress: onBrowseTopics, primary: true }}
          isDarkMode={isDarkMode}
          testID="continue-card-empty"
        />
      ) : (
        <View>
          {learning ? (
            <HomeRow
              first
              icon={learning.icon}
              title={learning.title}
              subtitle={learning.crumb}
              action={{ label: t("Continue"), onPress: onContinueLearning, primary: true }}
              isDarkMode={isDarkMode}
              testID="continue-card-learning"
            />
          ) : null}
          {curiosity ? (
            <HomeRow
              first={!learning}
              icon="chat-processing-outline"
              title={curiosity.title}
              subtitle={curiosity.subtitle}
              onPress={onContinueCuriosityAI}
              isDarkMode={isDarkMode}
              testID="continue-card-curiosity"
            />
          ) : null}
        </View>
      )}
    </HomeCard>
  );
};

export default ContinueCard;
